import { ChangeEvent, Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, Select, MenuItem, SelectChangeEvent, CardMedia, Input, FormHelperText } from "@mui/material";
import React from "react";
import { useCreateYogurtFlavorMutation, useUpdateYogurtFlavorMutation, useGetFlavourListQuery, useUploadFileMutation, YogurtFlavorItem } from "../store/api/commonAPI";
import { showToast, showError } from "../constants/toasts";
import Loader from "../constants/Loader";
import { PricingMethod } from "../constants/enums";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  yogurtId?: string;
  editFlavor?: YogurtFlavorItem | null;
  pricingMethod?: number;
}

export default function AddTypeFlavor({
  open,
  onClose,
  setOpen,
  yogurtId = "",
  editFlavor,
  pricingMethod = PricingMethod.PER_100G,
}: ModalProps) {
  const [flavorId, setFlavorId] = useState<string>("");
  const [image, setImage] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [pricePer100g, setPricePer100g] = useState<string>("");
  const [smallSize, setSmallSize] = useState<string>("");
  const [mediumSize, setMediumSize] = useState<string>("");
  const [largeSize, setLargeSize] = useState<string>("");
  const [errors, setErrors] = useState<{
    image?: string;
    flavorId?: string;
    pricePer100g?: string;
    smallSize?: string;
    mediumSize?: string;
    largeSize?: string;
  }>({});

  const isEditMode = !!editFlavor;

  // Fetch flavors list for dropdown
  const dropdownPayload = {
    page: 1,
    limit: 100, // Get all flavors
    yogurtId: yogurtId, // Filter by yogurt ID
  };
  console.log("📋 Flavor Dropdown API Payload:", dropdownPayload);
  const { data: flavorsData, refetch: refetchFlavors } = useGetFlavourListQuery(dropdownPayload);

  // Upload file and create/update yogurt flavor mutations
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [createYogurtFlavor, { isLoading: isCreating }] = useCreateYogurtFlavorMutation();
  const [updateYogurtFlavor, { isLoading: isUpdating }] = useUpdateYogurtFlavorMutation();

  const isLoading = isUploading || isCreating || isUpdating;

  // State to track existing image URL (for edit mode)
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");

  // Extract flavors from API response
  const flavors = React.useMemo(() => {
    if (flavorsData?.data?.flavour && Array.isArray(flavorsData.data.flavour)) {
      return flavorsData.data.flavour.filter((flavor: any) => flavor.isActive !== false);
    }
    return [];
  }, [flavorsData]);

  // Refetch dropdown list when modal opens
  useEffect(() => {
    if (open) {
      refetchFlavors();
    }
  }, [open, refetchFlavors]);

  // Reset form when modal opens/closes or when editFlavor changes
  useEffect(() => {
    if (!open) {
      setFlavorId("");
      setImage("");
      setImageFile(null);
      setFileName("");
      setPricePer100g("");
      setSmallSize("");
      setMediumSize("");
      setLargeSize("");
      setUploadedImageUrl("");
      setErrors({});
    } else if (editFlavor) {
      // Populate form with edit data
      const flavorDetailsId = editFlavor.flavourDetails?._id || "";
      setFlavorId(flavorDetailsId);
      setPricePer100g(editFlavor.pricePer100g?.toString() || "");
      setSmallSize(editFlavor.smallSize?.toString() || "");
      setMediumSize(editFlavor.mediumSize?.toString() || "");
      setLargeSize(editFlavor.largeSize?.toString() || "");
      
      // Handle image - keep original URL for API, format for display
      const existingImage = editFlavor.image || "";
      setUploadedImageUrl(existingImage); // Keep original URL for API
      
      // Format image URL for display
      const formattedImage = existingImage.startsWith('http') 
        ? existingImage 
        : existingImage 
          ? (existingImage.startsWith('/') ? existingImage : `/static/images/${existingImage}`)
          : "";
      setImage(formattedImage);
      setImageFile(null);
      setFileName("");
      setErrors({});
    } else {
      // Reset for add mode
      setFlavorId("");
      setImage("");
      setImageFile(null);
      setFileName("");
      setPricePer100g("");
      setSmallSize("");
      setMediumSize("");
      setLargeSize("");
      setUploadedImageUrl("");
      setErrors({});
    }
  }, [open, editFlavor]);

  const handleFlavorChange = (event: SelectChangeEvent) => {
    const value = event.target.value as string;
    setFlavorId(value);
    if (errors.flavorId) {
      setErrors((prev) => ({ ...prev, flavorId: undefined }));
    }
  };

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.includes("image")) {
        setErrors((prev) => ({ ...prev, image: "Please select a valid image file" }));
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, image: "Image size should be less than 5MB" }));
        return;
      }

      setImageFile(file);
      setFileName(file.name);
      setImage(URL.createObjectURL(file));
      // Clear image error when user selects a new image
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: undefined }));
      }
    }
  };

  const handlePriceChange = (setter: (value: string) => void, field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow positive numbers with decimals
    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      setter(value);
      if (errors[field as keyof typeof errors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: {
      image?: string;
      flavorId?: string;
      pricePer100g?: string;
      smallSize?: string;
      mediumSize?: string;
      largeSize?: string;
    } = {};

    // Validate image - required for add mode, optional for edit mode (if already has image)
    if (!isEditMode && !imageFile) {
      newErrors.image = "Please upload a flavor image";
    } else if (isEditMode && !imageFile && !uploadedImageUrl) {
      newErrors.image = "Please upload a flavor image";
    }

    // Validate flavor selection
    if (!flavorId || flavorId === "0") {
      newErrors.flavorId = "Please select a flavor";
    }

    // Validate based on pricing method
    if (pricingMethod === PricingMethod.PER_100G) {
      // Only validate price per 100g for PER_100G pricing
      if (!pricePer100g.trim()) {
        newErrors.pricePer100g = "Price per 100g is required";
      } else {
        const price = parseFloat(pricePer100g);
        if (isNaN(price) || price < 0) {
          newErrors.pricePer100g = "Price must be a positive number";
        }
      }
    } else if (pricingMethod === PricingMethod.SIZE_WISE) {
      // Validate size-wise prices for SIZE_WISE pricing
      if (!smallSize.trim()) {
        newErrors.smallSize = "Small size price is required";
      } else {
        const price = parseFloat(smallSize);
        if (isNaN(price) || price < 0) {
          newErrors.smallSize = "Price must be a positive number";
        }
      }

      if (!mediumSize.trim()) {
        newErrors.mediumSize = "Medium size price is required";
      } else {
        const price = parseFloat(mediumSize);
        if (isNaN(price) || price < 0) {
          newErrors.mediumSize = "Price must be a positive number";
        }
      }

      if (!largeSize.trim()) {
        newErrors.largeSize = "Large size price is required";
      } else {
        const price = parseFloat(largeSize);
        if (isNaN(price) || price < 0) {
          newErrors.largeSize = "Price must be a positive number";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    if (!yogurtId) {
      showError("Yogurt type ID is missing. Please try again.");
      return;
    }

    try {
      let imageUrl = uploadedImageUrl;

      // Step 1: Upload image if a new file is selected
      if (imageFile) {
        try {
          const uploadResult = await uploadFile({ file: imageFile }).unwrap();
          
          // Extract image URL from upload response (same pattern as other pages)
          imageUrl = uploadResult.data?.data || uploadResult.data || "";
          
          console.log("📤 Image upload response:", uploadResult);
          console.log("📷 Extracted image URL:", imageUrl);
          
          if (!imageUrl) {
            showError("Failed to get image URL from server.");
            return;
          }
        } catch (uploadError: any) {
          console.error("❌ Error uploading image:", uploadError);
          
          // Extract error message from upload API response
          const uploadErrorMessage =
            uploadError?.data?.message ||
            uploadError?.error?.data?.message ||
            uploadError?.data?.error ||
            uploadError?.message ||
            "Failed to upload image. Please try again.";
          
          showError(uploadErrorMessage);
          return;
        }
      }

      // If no image URL available, show error
      if (!imageUrl) {
        showError("Please upload an image");
        return;
      }

      // Step 2: Create or update yogurt flavor with image URL
      let response;
      
      if (isEditMode && editFlavor) {
        // Update existing yogurt flavor - only send fields that can be edited
        const payload: any = {
          id: editFlavor._id || editFlavor.id || "",
          image: imageUrl,
          isActive: editFlavor.isActive ?? true,
          featured: editFlavor.featured ?? false,
        };

        // Add pricing fields based on pricing method
        if (pricingMethod === PricingMethod.PER_100G) {
          payload.pricePer100g = parseFloat(pricePer100g) || 0;
        } else if (pricingMethod === PricingMethod.SIZE_WISE) {
          payload.smallSize = parseFloat(smallSize) || 0;
          payload.mediumSize = parseFloat(mediumSize) || 0;
          payload.largeSize = parseFloat(largeSize) || 0;
        }

        console.log("📤 Updating yogurt flavor with payload:", payload);
        response = await updateYogurtFlavor(payload).unwrap();
        console.log("✅ Update yogurt flavor response:", response);
      } else {
        // Create new yogurt flavor - include all required fields
        const basePayload: any = {
          image: imageUrl,
          yogurtId: yogurtId,
          flavorId: flavorId,
        };

        if (pricingMethod === PricingMethod.PER_100G) {
          basePayload.pricePer100g = parseFloat(pricePer100g) || 0;
        } else if (pricingMethod === PricingMethod.SIZE_WISE) {
          basePayload.smallSize = parseFloat(smallSize) || 0;
          basePayload.mediumSize = parseFloat(mediumSize) || 0;
          basePayload.largeSize = parseFloat(largeSize) || 0;
        }

        console.log("📤 Creating yogurt flavor with payload:", basePayload);
        response = await createYogurtFlavor(basePayload).unwrap();
        console.log("✅ Create yogurt flavor response:", response);
      }

      // Show success message from API response or default message
      const selectedFlavor = flavors.find((f: any) => (f._id || f.id) === flavorId);
      const flavorName = selectedFlavor?.name || editFlavor?.flavourDetails?.name || "flavor";
      const successMessage = response?.message || `Yogurt flavor "${flavorName}" has been ${isEditMode ? "updated" : "added"} successfully`;
      showToast(successMessage);

      // Refetch dropdown list to get latest data
      refetchFlavors();

      // Reset form and close modal
      setFlavorId("");
      setImage("");
      setImageFile(null);
      setFileName("");
      setPricePer100g("");
      setSmallSize("");
      setMediumSize("");
      setLargeSize("");
      setUploadedImageUrl("");
      setErrors({});
      setOpen(false);
      onClose(); // Call onClose to trigger refetch in parent
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} yogurt flavor:`, error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to ${isEditMode ? "update" : "add"} yogurt flavor. Please try again.`;

      showError(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFlavorId("");
      setImage("");
      setImageFile(null);
      setFileName("");
      setPricePer100g("");
      setSmallSize("");
      setMediumSize("");
      setLargeSize("");
      setUploadedImageUrl("");
      setErrors({});
      setOpen(false);
      onClose();
    }
  };

  return (
    <>
      <Loader isLoad={isLoading} />
      <Modal
        className="modal addType_modal"
        open={open}
        onClose={handleClose}
      >
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="modal_title">
              <h2>{isEditMode ? "Edit Flavor" : "Add Flavor"}</h2>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
                disabled={isLoading}
              >
                <CloseIcon />
              </button>
            </div>

            <form className="form" onSubmit={handleSubmit}>
            <div className="gap_p">
              <div className="control_group w_50">
                <label htmlFor="icon-button-file">
                  Upload Flavor Image<span className="req">*</span>
                </label>
                {image ? (
                  <div className="upload_image">
                    <div className="upload_image_holder no_radius v2">
                      <figure>
                        <CardMedia component="img" image={image} alt="photo" />
                      </figure>
                      <CloseIcon
                        onClick={() => {
                          setImage("");
                          setImageFile(null);
                          setFileName("");
                          setUploadedImageUrl("");
                          if (errors.image) {
                            setErrors((prev) => ({ ...prev, image: undefined }));
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={handleImageChange}
                      disabled={isLoading}
                    />
                    <span className="upload_image_holder no_radius">
                      <figure className="add_img">
                        <img src="/static/images/placeholder.png" alt="icon" />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
                {errors.image && (
                  <FormHelperText error style={{ marginTop: '4px', marginLeft: '14px' }}>
                    {errors.image}
                  </FormHelperText>
                )}
              </div>
              <div className="control_group">
                <label htmlFor="type-flavor">{isEditMode ? "Flavor" : "Select Flavor"}</label>
                {isEditMode ? (
                  <TextField
                    fullWidth
                    hiddenLabel
                    id="type-flavor"
                    value={editFlavor?.flavourDetails?.name || "N/A"}
                    disabled
                    InputProps={{
                      readOnly: true,
                      sx: {
                        color: '#000000',
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#000000',
                          color: '#000000',
                        },
                      },
                    }}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: '#000000',
                        color: '#000000',
                      },
                    }}
                  />
                ) : (
                  <>
                    <Select
                      fullWidth
                      hiddenLabel
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={flavorId}
                      onChange={handleFlavorChange}
                      error={!!errors.flavorId}
                      disabled={isLoading}
                    >
                      <MenuItem value="0" disabled>Choose a flavor</MenuItem>
                      {flavors.map((flavor: any) => (
                        <MenuItem key={flavor._id || flavor.id} value={flavor._id || flavor.id}>
                          {flavor.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.flavorId && (
                      <FormHelperText error style={{ marginTop: '4px', marginLeft: '14px' }}>
                        {errors.flavorId}
                      </FormHelperText>
                    )}
                  </>
                )}
              </div>

              {/* Show price per 100g only for PER_100G pricing method */}
              {pricingMethod === PricingMethod.PER_100G && (
                <div className="control_group">
                  <label htmlFor="price-per-100g">Price per 100g (OMR)</label>
                  <TextField 
                    fullWidth 
                    hiddenLabel 
                    placeholder="0.00" 
                    type="text"
                    value={pricePer100g}
                    onChange={handlePriceChange(setPricePer100g, "pricePer100g")}
                    error={!!errors.pricePer100g}
                    disabled={isLoading}
                  />
                  {errors.pricePer100g && (
                    <FormHelperText error style={{ marginTop: '4px', marginLeft: '14px' }}>
                      {errors.pricePer100g}
                    </FormHelperText>
                  )}
                </div>
              )}

              {/* Show size-wise prices only for SIZE_WISE pricing method */}
              {pricingMethod === PricingMethod.SIZE_WISE && (
                <>
                  <div className="control_group w_33">
                    <label htmlFor="small-size">Small Size Price (OMR)</label>
                    <TextField 
                      fullWidth 
                      hiddenLabel 
                      placeholder="0.00" 
                      type="text"
                      value={smallSize}
                      onChange={handlePriceChange(setSmallSize, "smallSize")}
                      error={!!errors.smallSize}
                      disabled={isLoading}
                    />
                    {errors.smallSize && (
                      <FormHelperText error style={{ marginTop: '4px', marginLeft: '14px' }}>
                        {errors.smallSize}
                      </FormHelperText>
                    )}
                  </div>

                  <div className="control_group w_33">
                    <label htmlFor="medium-size">Medium Size Price (OMR)</label>
                    <TextField 
                      fullWidth 
                      hiddenLabel 
                      placeholder="0.00" 
                      type="text"
                      value={mediumSize}
                      onChange={handlePriceChange(setMediumSize, "mediumSize")}
                      error={!!errors.mediumSize}
                      disabled={isLoading}
                    />
                    {errors.mediumSize && (
                      <FormHelperText error style={{ marginTop: '4px', marginLeft: '14px' }}>
                        {errors.mediumSize}
                      </FormHelperText>
                    )}
                  </div>

                  <div className="control_group w_33">
                    <label htmlFor="large-size">Large Size Price (OMR)</label>
                    <TextField 
                      fullWidth 
                      hiddenLabel 
                      placeholder="0.00" 
                      type="text"
                      value={largeSize}
                      onChange={handlePriceChange(setLargeSize, "largeSize")}
                      error={!!errors.largeSize}
                      disabled={isLoading}
                    />
                    {errors.largeSize && (
                      <FormHelperText error style={{ marginTop: '4px', marginLeft: '14px' }}>
                        {errors.largeSize}
                      </FormHelperText>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="footer_btn_flex">
              <Button
                type="button"
                className="btn btn_grey"
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isLoading}
              >
                {isLoading ? (isEditMode ? "Updating..." : "Adding...") : (isEditMode ? "Update Flavor" : "Add Flavor")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
    </>
  );
}
